import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDate, formatRelative } from "../lib/api";
import { fmtMoney } from "../components/ops/opsUtils";
import { Receipt, Plus, Search, Filter, CheckCircle2, Clock, XCircle, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import GeneratePayoffModal from "../components/payoffs/GeneratePayoffModal";
import { toast } from "sonner";

const STATUS_BADGES = {
  active:   { bg: "#DCFCE7", text: "#166534", label: "Active",   Icon: Clock },
  expired:  { bg: "#FEE2E2", text: "#B91C1C", label: "Expired",  Icon: XCircle },
  voided:   { bg: "#F3F4F6", text: "#4B5563", label: "Voided",   Icon: XCircle },
  settled:  { bg: "#DBEAFE", text: "#1E40AF", label: "Settled",  Icon: CheckCircle2 },
};

export default function PayoffsIndex() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/payoffs`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (r.client_name || "").toLowerCase().includes(t) ||
           (r.statement_number || "").toLowerCase().includes(t) ||
           (r.attorney_email || "").toLowerCase().includes(t);
  }), [rows, q, statusFilter]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.status === "active");
    return {
      active: active.length,
      active_total: active.reduce((s, r) => s + (r.final_amount || 0), 0),
      total: rows.length,
      total_value: rows.reduce((s, r) => s + (r.final_amount || 0), 0),
    };
  }, [rows]);

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="payoffs-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <Receipt className="w-3.5 h-3.5" /> Payoffs
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Payoff statements</h1>
          <p className="text-sm text-[#6B7280] mt-1">Generate, track, and send branded payoff letters in seconds.</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#0066FF] hover:bg-[#0052CC] h-10" data-testid="generate-payoff-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Generate payoff
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Active statements" value={stats.active} accent />
        <Stat label="Active value" value={fmtMoney(stats.active_total)} />
        <Stat label="All-time generated" value={stats.total} />
        <Stat label="Lifetime statement value" value={fmtMoney(stats.total_value)} />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, attorney, or statement #…" className="pl-9 h-9" data-testid="payoffs-search" />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Filter className="w-3.5 h-3.5 text-[#6B7280] mr-1" />
            {["", "active", "expired", "settled", "voided"].map((s) => (
              <button key={s || "all"} onClick={() => setStatusFilter(s)}
                data-testid={`filter-${s || "all"}`}
                className={`px-2.5 py-1 rounded-md font-medium transition-colors ${statusFilter === s ? "bg-[#0A2540] text-white" : "text-[#6B7280] hover:bg-[#F9FAFB]"}`}>
                {s ? s[0].toUpperCase() + s.slice(1) : "All"}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <th className="px-4 py-2.5 font-medium">Statement</th>
              <th className="px-4 py-2.5 font-medium">Client</th>
              <th className="px-4 py-2.5 font-medium">Attorney</th>
              <th className="px-4 py-2.5 font-medium text-right">Final amount</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Valid through</th>
              <th className="px-4 py-2.5 font-medium">Generated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6B7280]" data-testid="payoffs-loading">Loading payoff statements…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-16 text-center" data-testid="payoffs-empty">
                <div className="inline-flex flex-col items-center text-[#6B7280]">
                  <div className="w-12 h-12 rounded-full bg-[#F0F6FF] flex items-center justify-center mb-3"><FileText className="w-5 h-5 text-[#0066FF]" /></div>
                  <div className="text-sm font-medium text-[#0A2540]">No payoff statements yet</div>
                  <div className="text-xs mt-0.5">Click <span className="text-[#0066FF] font-medium">Generate payoff</span> to create your first one.</div>
                </div>
              </td></tr>
            ) : filtered.map((r) => {
              const cfg = STATUS_BADGES[r.status] || STATUS_BADGES.active;
              const Icon = cfg.Icon;
              return (
                <tr key={r.payoff_id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer" data-testid={`payoff-row-${r.payoff_id}`}>
                  <td className="px-4 py-3"><Link to={`/admin/payoffs/${r.payoff_id}`} className="text-[#0066FF] hover:underline tabular-nums">{r.statement_number || r.payoff_id}</Link></td>
                  <td className="px-4 py-3 text-[#0A2540] font-medium"><Link to={`/admin/payoffs/${r.payoff_id}`}>{r.client_name}</Link></td>
                  <td className="px-4 py-3 text-[#6B7280] text-xs">{r.attorney_email}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#0A2540] font-medium">{fmtMoney(r.final_amount)}{r.override_amount != null && <span className="ml-1.5 text-[10px] text-[#B45309] font-medium">OVERRIDE</span>}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: cfg.bg, color: cfg.text }}><Icon className="w-3 h-3" /> {cfg.label}</span></td>
                  <td className="px-4 py-3 text-[#6B7280] tabular-nums">{formatDate(r.valid_through)}</td>
                  <td className="px-4 py-3 text-[#6B7280] text-xs tabular-nums">{formatRelative(r.generated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <GeneratePayoffModal open={showModal} onClose={() => { setShowModal(false); load(); }} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">{label}</div>
      <div className={`tabular-nums ${accent ? "text-[#0066FF] font-display text-2xl font-semibold" : "text-[#0A2540] text-xl font-medium"}`}>{value}</div>
    </div>
  );
}
